/** Everything that does not earn a permanent tab slot. */
import { Card, Row, Rows, SectionTitle } from "../components/ui.js";
import { navigate } from "../routes.js";
import type { RouteId } from "../routes.js";
import { useApp } from "../state.js";
import { viewLimits } from "../view.js";

type Item = { id: RouteId; title: string; sub: string };

const BOOKS: Item[] = [
  { id: "daybook", title: "Day Book", sub: "every voucher, newest first" },
  { id: "sales", title: "Sales Register", sub: "invoices and monthly totals" },
  { id: "stock", title: "Stock Summary", sub: "closing quantity and value" },
  { id: "search", title: "Search", sub: "parties, items, voucher numbers" },
];

const INSIGHTS: Item[] = [
  { id: "dormant", title: "Customers who stopped buying", sub: "worth a phone call" },
  { id: "top-customers", title: "Top customers", sub: "who your revenue depends on" },
  { id: "top-items", title: "Top items", sub: "what actually sells" },
  { id: "dead-stock", title: "Not selling", sub: "capital sitting on the shelf" },
];

/** Only reachable with the advancedReports flag on — most books use none of these. */
const ADVANCED: Item[] = [
  { id: "cost-centres", title: "Cost Centres", sub: "allocation by centre and category" },
  { id: "godowns", title: "Godowns", sub: "item movement per location" },
  { id: "batches", title: "Batches & Expiry", sub: "what is expiring, and what already has" },
];

export function More() {
  const { snapshot, fresh, generatedAt, lock, flags, companies, connection, switchCompany } =
    useApp();
  const limits = viewLimits(snapshot);
  const shown = (items: Item[]) => items.filter((item) => !limits.hidden.has(item.id));

  return (
    <div className="space-y-5">
      {limits.note && (
        <div className="rounded-xl border border-line bg-accent-wash px-4 py-3 text-xs leading-relaxed text-ink-2">
          {limits.note}
        </div>
      )}
      {companies.length > 1 && (
        <div>
          <SectionTitle>Company</SectionTitle>
          <Card flush>
            <Rows>
              {companies.map((entry) => (
                <Row
                  key={entry.companyId}
                  title={entry.companyId}
                  sub={entry.companyId === connection?.companyId ? "showing now" : "tap to switch"}
                  right={
                    entry.companyId === connection?.companyId ? (
                      <span className="text-xs font-semibold text-accent">●</span>
                    ) : undefined
                  }
                  onClick={
                    entry.companyId === connection?.companyId
                      ? undefined
                      : () => void switchCompany(entry.companyId)
                  }
                />
              ))}
            </Rows>
          </Card>
        </div>
      )}

      <div>
        <SectionTitle>Insights</SectionTitle>
        <Card flush>
          <Rows>
            {shown(INSIGHTS).map((item) => (
              <Row
                key={item.id}
                title={item.title}
                sub={item.sub}
                onClick={() => navigate({ id: item.id })}
              />
            ))}
          </Rows>
        </Card>
      </div>

      <div>
        <SectionTitle>Books</SectionTitle>
        <Card flush>
          <Rows>
            {shown(BOOKS).map((item) => (
              <Row
                key={item.id}
                title={item.title}
                sub={item.sub}
                onClick={() => navigate({ id: item.id })}
              />
            ))}
          </Rows>
        </Card>
      </div>

      {flags.advancedReports && (
        <div>
          <SectionTitle>Cost centres and inventory</SectionTitle>
          <Card flush>
            <Rows>
              {shown(ADVANCED).map((item) => (
                <Row
                  key={item.id}
                  title={item.title}
                  sub={item.sub}
                  onClick={() => navigate({ id: item.id })}
                />
              ))}
            </Rows>
          </Card>
        </div>
      )}

      <div>
        <SectionTitle>This snapshot</SectionTitle>
        <Card>
          <dl className="space-y-2 text-sm">
            <Line label="Company" value={snapshot?.company.name ?? "—"} />
            <Line label="Age" value={fresh?.label ?? "—"} />
            <Line
              label="Generated"
              value={generatedAt ? new Date(generatedAt).toLocaleString() : "—"}
            />
            <Line label="Vouchers" value={String(snapshot?.vouchers.length ?? 0)} />
            <Line label="Ledgers" value={String(snapshot?.ledgers.length ?? 0)} />
            <Line label="Stock items" value={String(snapshot?.stockItems.length ?? 0)} />
          </dl>
        </Card>
      </div>

      <div>
        <SectionTitle>App</SectionTitle>
        <Card flush>
          <Rows>
            <Row
              title="Settings"
              sub="connection and this device"
              onClick={() => navigate({ id: "settings" })}
            />
            <Row title="Lock now" sub="forget the passphrase for this session" onClick={lock} />
          </Rows>
        </Card>
      </div>
    </div>
  );
}

function Line({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="shrink-0 text-ink-3">{label}</dt>
      <dd className="truncate text-right text-ink">{value}</dd>
    </div>
  );
}
